import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Link2, Share2, Copy, Globe } from "lucide-react";
import { api, exportUrl, imageUrl } from "@/lib/api";
import BeforeAfter from "@/components/BeforeAfter";
import QualityReport from "@/components/QualityReport";
import { toast } from "sonner";

export default function Detail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [inGallery, setInGallery] = useState(false);

  useEffect(() => {
    api.get(`/images/${id}`).then((r) => {
      setItem(r.data);
      if (r.data.shared && r.data.share_token) {
        setShareUrl(`${window.location.origin}/share/${r.data.share_token}`);
      }
      setInGallery(!!r.data.gallery);
    }).catch(() => setItem(null));
  }, [id]);

  const enableShare = async () => {
    const { data } = await api.post(`/images/${id}/share`, { enabled: true });
    setShareUrl(`${window.location.origin}/share/${data.share_token}`);
    setItem({ ...item, shared: true, share_token: data.share_token });
    toast.success("Lien public créé");
  };
  const disableShare = async () => {
    await api.post(`/images/${id}/share`, { enabled: false });
    setShareUrl(null);
    setInGallery(false);
    setItem({ ...item, shared: false, gallery: false });
    toast.success("Lien désactivé");
  };
  const copyShare = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Lien copié");
  };
  const toggleGallery = async () => {
    const enabled = !inGallery;
    const { data } = await api.post(`/images/${id}/gallery`, { enabled });
    setInGallery(data.gallery);
    if (data.gallery) {
      if (data.share_token) setShareUrl(`${window.location.origin}/share/${data.share_token}`);
      toast.success("Publiée dans la galerie communautaire");
    } else {
      toast.success("Retirée de la galerie");
    }
  };

  if (!item) return <div className="text-muted-foreground">Chargement…</div>;
  const r = item.report;
  return (
    <div className="space-y-6">
      <Link to="/library" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white" data-testid="back-to-library">
        <ArrowLeft className="w-4 h-4" /> Médiathèque
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">{r.mode_label}</div>
          <h2 className="mt-2 text-3xl font-light tracking-tight">{item.name}</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {item.tags?.map((t) => (
              <span key={t} className="text-[11px] font-mono px-2 py-0.5 rounded border border-white/10 text-muted-foreground">{t}</span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={exportUrl(id, "jpg")} data-testid="export-jpg" className="px-3 py-2 rounded-md border border-white/10 text-sm hover:bg-white/5 inline-flex items-center gap-2"><Download className="w-3.5 h-3.5" />JPG</a>
          <a href={exportUrl(id, "png")} data-testid="export-png" className="px-3 py-2 rounded-md border border-white/10 text-sm hover:bg-white/5 inline-flex items-center gap-2"><Download className="w-3.5 h-3.5" />PNG</a>
          <a href={exportUrl(id, "tif")} data-testid="export-tif" className="px-3 py-2 rounded-md border border-white/10 text-sm hover:bg-white/5 inline-flex items-center gap-2"><Download className="w-3.5 h-3.5" />TIFF</a>
          {!shareUrl ? (
            <button onClick={enableShare} data-testid="share-enable-btn" className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 glow-violet inline-flex items-center gap-2"><Share2 className="w-3.5 h-3.5" />Partager</button>
          ) : (
            <>
              <button onClick={copyShare} data-testid="share-copy-btn" className="px-3 py-2 rounded-md border border-violet-400/40 text-sm hover:bg-violet-500/10 inline-flex items-center gap-2"><Copy className="w-3.5 h-3.5" />Copier le lien</button>
              <button onClick={toggleGallery} data-testid="gallery-toggle-btn" className={`px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 ${inGallery ? "border border-emerald-400/40 bg-emerald-500/10 text-emerald-200" : "border border-white/10 hover:bg-white/5"}`}>
                <Globe className="w-3.5 h-3.5" />{inGallery ? "Dans la galerie" : "Publier dans la galerie"}
              </button>
              <button onClick={disableShare} data-testid="share-disable-btn" className="px-3 py-2 rounded-md border border-white/10 text-sm hover:bg-destructive/20 inline-flex items-center gap-2">Désactiver</button>
            </>
          )}
        </div>
      </div>

      {shareUrl && (
        <div className="rounded-md border border-violet-400/30 bg-violet-500/5 p-3 flex items-center gap-2" data-testid="share-url">
          <Link2 className="w-4 h-4 text-violet-300 flex-none" />
          <code className="text-xs font-mono truncate flex-1">{shareUrl}</code>
        </div>
      )}

      <BeforeAfter beforeSrc={imageUrl(id, "original")} afterSrc={imageUrl(id, "processed")} />
      <QualityReport report={r} />
      <div className="rounded-md border border-white/10 bg-card p-5">
        <div className="label-eyebrow mb-3">Pipeline appliqué</div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {r.steps?.map((s, i) => (
            <li key={i} className="text-sm flex items-start gap-3">
              <span className="font-mono text-[11px] text-violet-300 w-6">{String(i + 1).padStart(2, "0")}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
